<template>
    <div class="wrapper" :class="{ 'nav-open': $sidebar.showSidebar }">

        <button @click="forceShowSidebar = !forceShowSidebar"
            :class="{ 'mobile-nav-toggle': true, force: forceShowSidebar }">
            <i class="now-ui-icons text_align-center visible-on-sidebar-regular" />
        </button>

        <notifications />

        <side-bar ref="sidebar" backgroundColor="black" :phoneDisconnected="disconnected"
            :phoneConnecting="reconnecting" :force-show="forceShowSidebar"
            @toggle-force="forceShowSidebar = !forceShowSidebar">
            <template #links>
                <!--<user-menu />-->

                <div class="px-3 filter-bar-container">
                    <b-input ref="navFilterInput" type="search" placeholder="Filter Navigation" v-model="navQuery" class="form-control-sm"
                        @keydown.enter.native="navFilterSubmit"
                        @keydown.down.native.prevent="navigateFilteredItems('down')"
                        @keydown.up.native.prevent="navigateFilteredItems('up')"
                        @keydown.esc.native.prevent="handleFilterEscape"
                        @focus.native="handleFilterFocus"
                        @blur.native="handleFilterBlur" />
                </div>

                <sidebar-item v-for="(i, index) in filteredSidebarItems" :key="i.path" :link="i" :force-open="!!navQuery"
                    :class="{ 'nav-selected': isParentSelected(index) }"
                    @link-click="forceShowSidebar = false">
                    <template v-if="i.children?.length">
                        <sidebar-item v-for="(si, childIndex) in i.children" :key="si.path" :link="si"
                            :class="{ 'nav-selected': isChildSelected(index, childIndex) }"
                            @link-click="forceShowSidebar = false" @toggle-favorite="updateNavFavorites" />
                    </template>
                    <template v-else-if="i.name == 'Favorites'">
                        <div style="color: white; border: 2px dashed white; border-radius: 6px;"
                            class="mx-2 px-3 py-1 text-center">
                            <small>
                                To add or remove favorites,
                                <template v-if="isMacOs">⌘</template>
                                <template v-else>CTRL</template>
                                + Click sidebar navigation items.
                            </small>

                            <b-button @click="favoritesToFalse" size="sm">
                                Dismiss
                            </b-button>
                        </div>
                    </template>
                </sidebar-item>
            </template>
        </side-bar>
        <div class="main-panel">
            <top-navbar />
            <router-view name="header" />

            <div :class="{ content: !$route.meta.hideContent }" @click="toggleSidebar">
                <zoom-center-transition :duration="200" mode="out-in">
                    <!-- your content here -->
                    <router-view />
                </zoom-center-transition>
            </div>
            <content-footer v-if="!$route.meta.hideFooter" />
        </div>
    </div>
</template>

<script lang="ts">
/* eslint-disable no-new */
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";

function hasElement(className: string) {
    return document.getElementsByClassName(className).length > 0;
}

function initScrollbar(className: string) {
    if (hasElement(className)) {
        new PerfectScrollbar(`.${className}`);
    } else {
        // try to init it later in case this component is loaded async
        setTimeout(() => {
            initScrollbar(className);
        }, 100);
    }
}

import TopNavbar from "./TopNavbar.vue";
import ContentFooter from "./ContentFooter.vue";
//import UserMenu from "./Extra/UserMenu.vue";
import { ZoomCenterTransition } from "vue2-transitions";
import { Component, Vue, Watch } from "vue-property-decorator";
import { PhoneStats } from "@/models/PhoneStats";

import SideBar from "@/components/SidebarPlugin/SideBar.vue";
import SidebarItem from "@/components/SidebarPlugin/SidebarItem.vue";
import { getSidebarItems, NavigationItem } from "@/config/navigationItems";

@Component({
    components: {
        TopNavbar,
        ContentFooter,
        //UserMenu,
        ZoomCenterTransition,
        SideBar,
        SidebarItem
    },
})
export default class Dashboard extends Vue {

    protected navQuery?: string = "";
    protected forceShowSidebar: boolean = false;
    protected selectedNavIndex: number = -1;
    protected selectedNavParentIndex: number = -1;
    protected selectedNavChildIndex: number = -1;
    protected filterHasFocus: boolean = false;

    mounted() {
        const docClasses = document.body.classList;
        const isWindows = navigator.platform.startsWith("Win");
        if (isWindows) {
            // if we are on windows OS we activate the perfectScrollbar function
            initScrollbar("sidebar");
            initScrollbar("sidebar-wrapper");

            docClasses.add("perfect-scrollbar-on");
        } else {
            docClasses.add("perfect-scrollbar-off");
        }

        this.favoriteItems = JSON.parse(window.localStorage.getItem("nav-favorites") ?? "[]");
        
        // Listen for sidebar minimize state changes
        if (this.$sidebar && this.$sidebar.eventBus) {
            this.$sidebar.eventBus.$on('sidebar-minimize-changed', this.handleMinimizeChange);
        }
        
        // Load and apply saved minimize state
        this.loadSavedMinimizeState();
    }

    @Watch("$root.data.dataHubState") onConnectionStatusChange() {
        console.log("dataHubStatus = " + this.$root.$data.dataHubState);
    }

    @Watch("sidebarMinimizedSetting")
    onMinimizeSettingChanged(newValue: string | undefined) {
        if (newValue !== undefined && this.$sidebar) {
            const shouldBeMinimized = newValue === 'true';
            const currentlyMinimized = this.$sidebar.isMinimized;
            
            if (shouldBeMinimized !== currentlyMinimized) {
                console.log(`Syncing sidebar minimize state from ${currentlyMinimized} to ${shouldBeMinimized}`);
                this.$sidebar.setMinimizeState(shouldBeMinimized, false);
            }
        }
    }

    @Watch("$root.$data.userSettings", { immediate: true })
    onUserSettingsLoaded() {
        this.loadSavedMinimizeState();
    }

    get disconnected(): boolean {
        return this.$root.$data.dataHubState == "Disconnected" || this.$root.$data.dataHubState == "Closed";
    }

    get reconnecting(): boolean {
        return this.$root.$data.dataHubState == "Connecting" || this.$root.$data.dataHubState == "Reconnecting";
    }


    toggleSidebar() {
        if ((this.$refs?.sidebar as any)?.showSidebar) {
            (this.$refs!.sidebar as any).displaySidebar(false);
        }
    }

    get printQueueName() {
        return this.$pageNames.printQueue ?? "Print Queue";
    }

    get stalePrintQueueName() {
        return this.$pageNames.stalePrintQueue ?? "Stale Print Queue";
    }

    get sidebarItems(): NavigationItem[] {
        return getSidebarItems(this.printQueueName, this.stalePrintQueueName);
    }

    get allSidebarItemChildren() {
        return this.sidebarItems.map(i => i.children).flat();
    }

    get allSidebarItems() {

        if (this.favoriteItems === false) return this.sidebarItems;

        const favorites = this.favoriteItems.map((fi: any) => {
            const path = fi.path ? fi.path : fi;

            const cat = this.sidebarItems.find(sc => sc.children?.find((c: any) => c.path === path));
            const navItem = cat?.children?.find((i: any) => i.path === path);

            const hasMultipleSameName = this.allSidebarItemChildren.filter(i => i?.name?.toLowerCase() === navItem?.name?.toLowerCase()).length > 1;

            return {
                ...navItem,
                subtitle: hasMultipleSameName ? cat?.name : undefined,
            };
        });

        return [{ name: "Favorites", children: favorites, icon: "fas fa-heart" }, ...this.sidebarItems];
    }

    @Watch("navQuery")
    resetSidebarScrollPosition() {
        const sidebar = (this.$refs?.sidebar as any)?.$el.querySelector('.sidebar-wrapper');
        if (!sidebar) return;
        sidebar.scrollTop = 0;
        this.clearNavSelection();
    }

    get permissionedSidebarItems() {
        const permissionedItems: any[] = [];

        this.allSidebarItems.forEach(i => {
            if (this.menuItemMissingPermission(i)) return;

            const hasChildren = !!i.children?.length;

            if (!hasChildren) {
                permissionedItems.push(i);
                return;
            }

            const isFavorites = i.name === "Favorites";

            const permissionedChildren = i.children?.filter((ci: any) => !this.menuItemMissingPermission(ci, i));

            if (!permissionedChildren?.length) {
                if (isFavorites) permissionedItems.push({ ...i, children: [] });
                return;
            }

            permissionedItems.push({ ...i, children: permissionedChildren });
        });

        return permissionedItems;
    }

    get filteredSidebarItems() {
        if (!this.navQuery) return this.permissionedSidebarItems;

        const filteredItems: any = [];

        this.permissionedSidebarItems.forEach(i => {
            if (this.menuItemHasFilterMatch(i)) {
                filteredItems.push(i);
                return;
            }

            const filteredChildren = i.children?.filter((ci: any) => this.menuItemHasFilterMatch(ci, i));

            if (!filteredChildren?.length) return;

            filteredItems.push({ ...i, children: filteredChildren });
        });

        return filteredItems;
    }

    get navQueryTerms() {
        return this.navQuery?.toLowerCase().split(" ");
    }

    private menuItemMissingPermission(item: any, parent?: any) {
        if (item.name === "Favorites") return false;
        const hasPermissionDefined = item.permission || parent?.permission;
        const hasAnyPermissionDefined = item.anyPermission || parent?.anyPermission;

        if (!hasPermissionDefined && !hasAnyPermissionDefined) return false;

        if (hasPermissionDefined) {
            return !this.$permissions.some(p => p.permissionCodeName.toLowerCase() === item.permission.toLowerCase());
        }

        return !this.$permissions.some(p => item.anyPermission.some((ap: string) => ap.toLowerCase() === p.permissionCodeName.toLowerCase()));
    }

    private menuItemHasFilterMatch(item: any, parent?: any) {
        return this.navQueryTerms?.every(t => (item.name + parent?.name + item.keywords + item.name?.match(/\b(\w)/g).join('')).toLowerCase().includes(t));
    }

    protected favoriteItems: any = [];

    updateNavFavorites(favorites: any) {
        this.favoriteItems = favorites;
    }

    favoritesToFalse() {
        this.favoriteItems = false;
        window.localStorage.setItem("nav-favorites", JSON.stringify(false));
    }

    handleMinimizeChange(isMinimized: boolean) {
        if ((this as any).$saveUserSetting) {
            (this as any).$saveUserSetting('sidebar-minimized', isMinimized.toString());
        }
        this.$set((this.$root as any).$data.userSettings, 'sidebar-minimized', isMinimized.toString());
    }

    loadSavedMinimizeState() {
        const userSettings = (this.$root as any).$data.userSettings || {};
        const savedMinimizedState = userSettings['sidebar-minimized'];
        
        if (savedMinimizedState !== undefined) {
            const isMinimized = savedMinimizedState === 'true';
            if (this.$sidebar && this.$sidebar.setMinimizeState) {
                this.$sidebar.setMinimizeState(isMinimized, true);
            }
        }
    }

    navFilterSubmit() {
        if (!this.navQuery?.trim() && this.selectedNavIndex === -1) return;
        
        // If we have a selected item, navigate to it
        if (this.selectedNavParentIndex >= 0) {
            const parent = this.filteredSidebarItems[this.selectedNavParentIndex];
            if (this.selectedNavChildIndex >= 0 && parent.children?.[this.selectedNavChildIndex]) {
                this.$router.push(parent.children[this.selectedNavChildIndex].path);
                this.clearNavSelection();
                this.blurNavFilter();
                this.focusMainContent();
                return;
            } else if (parent.path) {
                this.$router.push(parent.path);
                this.clearNavSelection();
                this.blurNavFilter();
                this.focusMainContent();
                return;
            }
        }
        
        // Otherwise navigate to first item as before
        const first = this.filteredSidebarItems[0];
        if (first?.children?.length) {
            this.$router.push(first.children[0].path);
        } else if (first?.path) {
            this.$router.push(first.path);
        }
        this.clearNavSelection();
        this.blurNavFilter();
        this.focusMainContent();
    }

    navigateFilteredItems(direction: 'up' | 'down') {
        const allItems = this.getAllNavigableItems();
        if (allItems.length === 0) return;

        let currentIndex = this.selectedNavIndex;
        
        if (direction === 'down') {
            currentIndex = currentIndex < allItems.length - 1 ? currentIndex + 1 : 0;
        } else {
            currentIndex = currentIndex > 0 ? currentIndex - 1 : allItems.length - 1;
        }
        
        this.selectedNavIndex = currentIndex;
        const item = allItems[currentIndex];
        if (item) {
            this.selectedNavParentIndex = item.parentIndex;
            this.selectedNavChildIndex = item.childIndex;
            this.scrollToSelectedItem();
        }
    }

    getAllNavigableItems() {
        const items: any[] = [];
        
        this.filteredSidebarItems.forEach((parent, parentIndex) => {
            // Add parent if it has a path (is navigable)
            if (parent.path && parent.name !== 'Favorites') {
                items.push({
                    parentIndex,
                    childIndex: -1,
                    element: parent
                });
            }
            
            // Add children
            if (parent.children?.length) {
                parent.children.forEach((child: any, childIndex: number) => {
                    items.push({
                        parentIndex,
                        childIndex,
                        element: child
                    });
                });
            }
        });
        
        return items;
    }

    isParentSelected(index: number): boolean {
        return this.filterHasFocus && this.selectedNavParentIndex === index && this.selectedNavChildIndex === -1;
    }

    isChildSelected(parentIndex: number, childIndex: number): boolean {
        return this.filterHasFocus && this.selectedNavParentIndex === parentIndex && this.selectedNavChildIndex === childIndex;
    }

    clearNavSelection() {
        this.selectedNavIndex = -1;
        this.selectedNavParentIndex = -1;
        this.selectedNavChildIndex = -1;
    }

    handleFilterEscape() {
        if (this.navQuery) {
            this.navQuery = '';
            this.clearNavSelection();
        } else {
            this.blurNavFilter();
            this.focusMainContent();
        }
    }

    blurNavFilter() {
        (this.$refs.navFilterInput as any)?.$el?.querySelector('input')?.blur();
    }

    handleFilterFocus() {
        this.filterHasFocus = true;
    }

    handleFilterBlur() {
        this.filterHasFocus = false;
        // Clear selection when losing focus
        this.clearNavSelection();
    }

    focusMainContent() {
        // Focus the main content area or first focusable element
        const mainPanel = document.querySelector('.main-panel .content');
        if (mainPanel) {
            // Try to find a focusable element in the main content
            const focusable = mainPanel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') as HTMLElement;
            if (focusable) {
                focusable.focus();
            } else {
                // If no focusable element, make the content area focusable temporarily and focus it
                (mainPanel as HTMLElement).setAttribute('tabindex', '-1');
                (mainPanel as HTMLElement).focus();
                // Remove tabindex after focus to not interfere with normal tab navigation
                setTimeout(() => {
                    (mainPanel as HTMLElement).removeAttribute('tabindex');
                }, 0);
            }
        }
    }

    scrollToSelectedItem() {
        // Scroll the selected item into view, accounting for the sticky filter bar
        this.$nextTick(() => {
            const selectedElement = document.querySelector('.sidebar .nav-selected');
            const sidebarWrapper = document.querySelector('.sidebar-wrapper');
            const filterBar = document.querySelector('.filter-bar-container');
            
            if (selectedElement && sidebarWrapper && filterBar) {
                const filterBarHeight = filterBar.offsetHeight;
                const buffer = 10; // Small buffer for visual separation
                const requiredOffset = filterBarHeight + buffer;
                
                const sidebarRect = sidebarWrapper.getBoundingClientRect();
                const selectedRect = selectedElement.getBoundingClientRect();
                
                // Check if element is hidden behind filter bar or needs scrolling
                const elementTopRelativeToSidebar = selectedRect.top - sidebarRect.top;
                const elementBottomRelativeToSidebar = selectedRect.bottom - sidebarRect.top;
                
                let scrollTop = sidebarWrapper.scrollTop;
                
                // If element is hidden behind filter bar, scroll it into view below the filter bar
                if (elementTopRelativeToSidebar < requiredOffset) {
                    scrollTop = sidebarWrapper.scrollTop - (requiredOffset - elementTopRelativeToSidebar);
                }
                // If element is below visible area, scroll it into view
                else if (elementBottomRelativeToSidebar > sidebarRect.height) {
                    scrollTop = sidebarWrapper.scrollTop + (elementBottomRelativeToSidebar - sidebarRect.height);
                }
                
                // Only scroll if needed
                if (scrollTop !== sidebarWrapper.scrollTop) {
                    sidebarWrapper.scrollTo({ top: scrollTop, behavior: 'smooth' });
                }
            }
        });
    }

    get isMacOs() {
        return navigator?.userAgent?.indexOf("Mac") > -1;
    }

    get sidebarMinimizedSetting(): string | undefined {
        return (this.$root as any).$data.userSettings?.['sidebar-minimized'];
    }

    beforeDestroy() {
        if (this.$sidebar && this.$sidebar.eventBus) {
            this.$sidebar.eventBus.$off('sidebar-minimize-changed', this.handleMinimizeChange);
        }
    }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped lang="scss">
$scaleSize: 0.95;

@keyframes zoomIn95 {
    from {
        opacity: 0;
        transform: scale3d($scaleSize, $scaleSize, $scaleSize);
    }

    to {
        opacity: 1;
    }
}

.main-panel .zoomIn {
    animation-name: zoomIn95;
}

@keyframes zoomOut95 {
    from {
        opacity: 1;
    }

    to {
        opacity: 0;
        transform: scale3d($scaleSize, $scaleSize, $scaleSize);
    }
}

.main-panel .zoomOut {
    animation-name: zoomOut95;
}

::v-deep(.sidebar input) {
    background: black;
    border: 1px solid black;
    color: white;

    &:hover,
    &:active,
    &:focus {
        border: 1px solid gray;
        background: black;
        color: white;
    }
}

.mobile-nav-toggle {
    position: fixed;
    top: 5px;
    left: 5px;
    z-index: 1100;
    border-radius: 50%;
    padding: 11px;
    border: 1px solid white;
    background: rgba(0, 0, 0, .65);
    color: white;
    display: flex;
    -webkit-backdrop-filter: blur(5px);
    backdrop-filter: blur(5px);
    box-shadow: 0 0 5px rgba(0, 0, 0, .7);
    transition: all .3s;

    &.force {
        left: 202px;
        top: 12px;
        background: transparent;
    }
}

.filter-bar-container {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #212120;
    width: calc(100% - 15px);
    margin-top: -25px;
    padding-top: 15px;
    padding-bottom: 15px;

    input {
        background: rgba(0, 0, 0, .95);
        -webkit-backdrop-filter: blur(5px);
        backdrop-filter: blur(5px);
        padding: 5px;
        width: calc(100% + 15px);
    }

    input:placeholder-shown {
        text-align: center;
    }

    input:focus:placeholder-shown {
        text-align: left;
    }
}

@media screen and (min-width: 991px) {
    .mobile-nav-toggle {
        display: none;
    }
}

::v-deep(.sidebar .nav-selected) {
    background: rgba(0, 162, 255, 0.2);
    border-radius: 4px;
    
    > a {
        color: #00a2ff !important;
    }
}

::v-deep(.sidebar .nav-selected.active) {
    background: rgba(0, 162, 255, 0.3);
}
</style>
